import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { lt } from 'date-fns/locale';

const WEEKDAYS = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

const toIso = (date) => format(date, 'yyyy-MM-dd');

const formatLt = (iso) => {
  if (!iso) return '';
  return format(parseISO(iso), 'yyyy-MM-dd', { locale: lt });
};

export default function ServicePeriodPicker({ startDate, endDate, onChange }) {
  const initialMonth = startDate ? parseISO(startDate) : new Date();
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(initialMonth));
  const [expanded, setExpanded] = useState(false);

  const start = startDate ? parseISO(startDate) : null;
  const end = endDate ? parseISO(endDate) : null;
  const hasRange = Boolean(startDate && endDate);

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const handleDayClick = (day) => {
    const iso = toIso(day);

    if (!startDate) {
      onChange({ startDate: iso, endDate: iso });
      return;
    }

    if (startDate && endDate && startDate === endDate) {
      const single = parseISO(startDate);
      if (isSameDay(day, single)) return;
      const rangeStart = isBefore(day, single) ? day : single;
      const rangeEnd = isAfter(day, single) ? day : single;
      onChange({ startDate: toIso(rangeStart), endDate: toIso(rangeEnd) });
      setExpanded(false);
      return;
    }

    if (startDate && endDate && startDate !== endDate) {
      onChange({ startDate: iso, endDate: iso });
    }
  };

  const isSelected = (day) => {
    if (!start || !end) return false;
    if (isSameDay(start, end)) return isSameDay(day, start);
    return isWithinInterval(day, { start, end });
  };

  const isRangeEdge = (day) =>
    (start && isSameDay(day, start)) || (end && isSameDay(day, end));

  const periodLabel = hasRange
    ? startDate === endDate
      ? formatLt(startDate)
      : `${formatLt(startDate)} – ${formatLt(endDate)}`
    : 'Nepasirinkta';

  const handleClear = (event) => {
    event.stopPropagation();
    onChange({ startDate: '', endDate: '' });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '4px !important',
        '&:before': { display: 'none' },
        bgcolor: 'background.paper',
        flexShrink: 0,
        '& .MuiAccordion-region': { overflow: 'visible' },
        '& .MuiCollapse-root': { overflow: 'visible' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 48,
          px: 1.5,
          '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1 }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
            Paslaugų teikimo laikotarpis
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              (neprivaloma)
            </Typography>
          </Typography>
          <Typography
            variant="body2"
            color={hasRange ? 'text.primary' : 'text.secondary'}
            noWrap
            sx={{ mt: 0.25 }}
          >
            {periodLabel}
          </Typography>
        </Box>
        {hasRange && (
          <Button size="small" onClick={handleClear} sx={{ flexShrink: 0, mr: 0.5 }}>
            Išvalyti
          </Button>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5, overflow: 'visible' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Paspauskite pradžios dieną, tada pabaigos dieną. Kalendorius suskleidžiamas automatiškai.
        </Typography>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <IconButton size="small" onClick={() => setViewMonth((m) => subMonths(m, 1))} aria-label="Ankstesnis mėnuo">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {format(viewMonth, 'LLLL yyyy', { locale: lt })}
            </Typography>
            <IconButton size="small" onClick={() => setViewMonth((m) => addMonths(m, 1))} aria-label="Kitas mėnuo">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.25,
              mb: 0.5
            }}
          >
            {WEEKDAYS.map((label) => (
              <Typography
                key={label}
                variant="caption"
                sx={{ textAlign: 'center', fontWeight: 600, color: 'text.secondary', py: 0.25 }}
              >
                {label}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25 }}>
            {days.map((day) => {
              const inMonth = isSameMonth(day, viewMonth);
              const selected = isSelected(day);
              const edge = isRangeEdge(day);
              return (
                <Box
                  key={toIso(day)}
                  component="button"
                  type="button"
                  onClick={() => handleDayClick(day)}
                  sx={{
                    border: 'none',
                    borderRadius: 1,
                    py: 0.75,
                    fontSize: 13,
                    cursor: 'pointer',
                    bgcolor: selected ? (edge ? 'primary.main' : 'primary.light') : 'transparent',
                    color: selected ? (edge ? 'primary.contrastText' : 'primary.dark') : inMonth ? 'text.primary' : 'text.disabled',
                    fontWeight: edge ? 700 : 400,
                    opacity: inMonth ? 1 : 0.45,
                    '&:hover': {
                      bgcolor: selected ? (edge ? 'primary.dark' : 'primary.light') : 'action.hover'
                    }
                  }}
                >
                  {format(day, 'd')}
                </Box>
              );
            })}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
